/* eslint-disable no-param-reassign */
/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable new-cap */
/* eslint-disable consistent-return */
import { createStructuredSelector } from 'reselect';
import ggStore from 'ggApp/modules/store';
import { allSettled } from 'ggApp/utils/promise';
import { isFunction } from 'ggApp/utils/function';
import { CATALOGUE_PAGE_TYPE } from 'ggApp/modules/contentful/pages/ListingPageSeoBlock';
import { pageService, entryService } from '../../services';
import {
    GET_CONTENTFUL_PAGE,
    GET_CONTENTFUL_PAGE_ERROR,
    GET_CONTENTFUL_PAGE_SUCCESS,
} from '../constants';
import { isContentfulPage, isOptionalContent } from '../../utils';

import { CfPartnerProductPage } from '../../pages';
import { CfDiscountBanner } from '../../models/DiscountBanner';

/**
 * getPageWithLanguageAndStore
 * This action creator gets page class or function that returns page type,
 * language and current store value.
 * It loads given page from contentful and dispatch an action with the page data.
 * @param {*} getPageType
 * @param {string} language
 * @param {string} store
 * @returns {Promise}
 * @example
 * // getPageType as a function has the following structure:
 * function getPageType(reduxStore) {
 *     // ... some actions to get page type class
 *     return pageType;
 * }
 */
function getPageWithLanguageAndStore(getPageType, language, rest = {}) {
    const { store } = rest;
    return (dispatch, getState) => {
        let pageType = getPageType;

        if (!pageType || typeof pageType !== 'function') {
            return undefined;
        }

        // check if it's not Model class, then it needs to be function that returns Model class
        if (!isContentfulPage(getPageType)) {
            pageType = getPageType(getState());
        }

        dispatch({ type: GET_CONTENTFUL_PAGE });
        return pageService
            .getContentfulPage(pageType, language, rest)
            .then((page) => {
                dispatch({
                    type: GET_CONTENTFUL_PAGE_SUCCESS,
                    pageName: pageType.pageName,
                    data: page.data,
                });
            })
            .catch((error) => {
                dispatch({
                    type: GET_CONTENTFUL_PAGE_ERROR,
                    error,
                });
                if (error.message === '404' && !isOptionalContent(getPageType.config)) {
                    return Promise.reject({
                        code: 404,
                        storeCodeParam: store,
                        languageParam: language,
                    });
                }
            });
    };
}

export const fetchPlpPageData = (dispatch, getState) => {
    const { userChosenLanguage, storeActive } = createStructuredSelector({
        userChosenLanguage: ggStore.selectors.userChosenLanguage(),
        storeActive: ggStore.selectors.storeActive,
    })(getState());

    dispatch({ type: GET_CONTENTFUL_PAGE });

    allSettled([
        entryService.getCfStoreSpecificEntry(CfPartnerProductPage.contentType, storeActive.code, {
            locale: userChosenLanguage,
        }),
        entryService.getCfStoreSpecificEntry(
            CfPartnerProductPage.contentType,
            CfPartnerProductPage.defaultStoreCode,
            {
                locale: userChosenLanguage,
            },
        ),
    ]).then(([storeBasedResult, defaultResult]) => {
        let entity;
        if (storeBasedResult.status === 'fulfilled') {
            entity = storeBasedResult.value;
        } else if (defaultResult.status === 'fulfilled') {
            entity = defaultResult.value;
        } else {
            return dispatch({
                type: GET_CONTENTFUL_PAGE_ERROR,
                error: defaultResult.reason,
            });
        }

        const contentfulData = new CfPartnerProductPage(entity);

        dispatch({
            type: GET_CONTENTFUL_PAGE_SUCCESS,
            pageName: CfPartnerProductPage.pageName,
            data: contentfulData,
        });
    });
};

/**
 * getPage
 * This action creator gets page class
 * or function that returns page type
 * and loads `language` from router parameters
 * via `needs` redux middleware.
 * @param {*} getPageType
 * @example
 * // getPageType as a function has the following structure:
 * function getPageType(reduxStore) {
 *     // ... some actions to get page type class
 *     return pageType;
 * }
 */
export const getPage = (getPageType) => ({ language }) =>
    getPageWithLanguageAndStore(getPageType, language);

/**
 * getPageFromCurrentStore
 * This action creator gets page class
 * or function that returns page type
 * and loads `language`, and filter information such as store, pageType, pageSlug
 * from router parameters via `needs` redux middleware.
 * @param {*} getPageType
 * @example
 * // getPageType as a function has the following structure:
 * function getPageType(reduxStore) {
 *     // ... some actions to get page type class
 *     return pageType;
 * }
 */
export const getPageFromCurrentStore = (getPageType) => ({ language, ...rest }) =>
    getPageWithLanguageAndStore(getPageType, language, rest);

/**
 * getCfContentInMainSpa
 * This action creator gets page class
 * or function that returns page type
 * @param {*} getPageType
 * it's intended to be used outside of cataloge.js
 * wherever we can't access the language from router params
 */
export const getCfContentInMainSpa = (getPageType) => (dispatch, getState) => {
    const { userChosenLanguage } = createStructuredSelector({
        userChosenLanguage: ggStore.selectors.userChosenLanguage(),
    })(getState());
    let pageType = getPageType;
    if (!isContentfulPage(getPageType)) {
        pageType = getPageType(getState());
    }
    dispatch({ type: GET_CONTENTFUL_PAGE });
    return entryService
        .getCfEntry(getPageType.config.contentType, {
            locale: userChosenLanguage,
        })
        .then((cfEntry) => {
            const page = new pageType(pageType.mapCfEntry(cfEntry));
            dispatch({
                type: GET_CONTENTFUL_PAGE_SUCCESS,
                pageName: pageType.pageName,
                data: page.data,
            });
        })
        .catch((error) => {
            dispatch({
                type: GET_CONTENTFUL_PAGE_ERROR,
                error,
            });
            if (error.message === '404' && !isOptionalContent(getPageType.config)) {
                return Promise.reject({
                    code: 404,
                    languageParam: userChosenLanguage,
                });
            }
        });
};

export const getCFDiscountBanner = (rest) => {
    return (dispatch, getState) => {
        const { storeActive, userChosenLanguage } = createStructuredSelector({
            storeActive: ggStore.selectors.storeActive,
            userChosenLanguage: ggStore.selectors.userChosenLanguage(),
        })(getState());
        dispatch({ type: GET_CONTENTFUL_PAGE });
        const userLanguage = rest?.language || userChosenLanguage;

        return pageService
            .getContentfulPage(CfDiscountBanner, userLanguage, {
                store: storeActive.code,
            })
            .then((page) => {
                dispatch({
                    type: GET_CONTENTFUL_PAGE_SUCCESS,
                    pageName: CfDiscountBanner.pageName,
                    data: page.data,
                });
            })
            .catch((error) => {
                dispatch({
                    type: GET_CONTENTFUL_PAGE_ERROR,
                    error,
                });
            });
    };
};
export const getCFPartnerDiscountBanner = (rest) => {
    return (dispatch, getState) => {
        const { storeActive, userChosenLanguage } = createStructuredSelector({
            storeActive: ggStore.selectors.storeActive,
            userChosenLanguage: ggStore.selectors.userChosenLanguage(),
        })(getState());
        dispatch({ type: GET_CONTENTFUL_PAGE });
        const userLanguage = rest?.language || userChosenLanguage;

        const fallback = () => {
            return pageService
                .getContentfulPage(CfDiscountBanner, userLanguage, {
                    store: 'partner',
                })
                .then((page) => {
                    dispatch({
                        type: GET_CONTENTFUL_PAGE_SUCCESS,
                        pageName: CfDiscountBanner.pageName,
                        data: page.data,
                    });
                })
                .catch((error) => {
                    throw error;
                });
        };

        return pageService
            .getContentfulPageDiscount(CfDiscountBanner, userLanguage, {
                store: storeActive.code,
            })
            .then((page) => {
                if (!page) {
                    fallback();
                } else {
                    dispatch({
                        type: GET_CONTENTFUL_PAGE_SUCCESS,
                        pageName: CfDiscountBanner.pageName,
                        data: page.data,
                    });
                }
            });
    };
};

export const getSeoForCataloguePage = (pageClass) => (params) => {
    return (dispatch) => {
        if (!isFunction(pageClass)) {
            return undefined;
        }

        const { language, subCategorySlug, categorySlug } = params || {};
        const isCategory = ![CATALOGUE_PAGE_TYPE.TRENDING, CATALOGUE_PAGE_TYPE.DEALS].includes(
            pageClass.contentType,
        );

        if (isCategory && isFunction(pageClass.setContentType)) {
            if (subCategorySlug) {
                pageClass.setContentType(subCategorySlug);
            } else if (categorySlug) {
                pageClass.setContentType(categorySlug);
            }
        }
        dispatch({ type: GET_CONTENTFUL_PAGE });
        return pageService
            .getContentfulPage(pageClass, language, {})
            .then((page) => {
                dispatch({
                    type: GET_CONTENTFUL_PAGE_SUCCESS,
                    pageName: pageClass.pageName,
                    data: page.data,
                });
            })
            .catch(() => {
                dispatch({
                    type: GET_CONTENTFUL_PAGE_SUCCESS,
                    pageName: pageClass.pageName,
                    data: null,
                });
            });
    };
};
